// C:\Users\EYG\fynwise-website\src\components\magicui\spotlight.tsx
"use client";

import React, { useRef, useState, useEffect, useCallback } from "react";

type SpotlightProps = {
  children: React.ReactNode;
  className?: string;
};

export default function Spotlight({
  children,
  className = "",
}: SpotlightProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Fare konumu
  const [mouse, setMouse] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // Container ölçüleri
  const containerSize = useRef<{ w: number; h: number }>({ w: 0, h: 0 });

  // Child elementlerin referansı
  const [boxes, setBoxes] = useState<Array<HTMLElement>>([]);
  
  // Cihaz tipini algılama
  const [isMobile, setIsMobile] = useState<boolean>(false);

  // onMouseMove fonksiyonunu useCallback ile sarmalıyoruz
  const onMouseMove = useCallback(() => {
    if (isMobile) return; // Mobilde işlem yapma
    
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const { w, h } = containerSize.current;
    const x = mouse.x;
    const y = mouse.y;
    const inside = x < w && x > 0 && y < h && y > 0;

    if (inside) {
      boxes.forEach((box) => {
        const boxX = -(box.getBoundingClientRect().left - rect.left) + mouse.x;
        const boxY = -(box.getBoundingClientRect().top - rect.top) + mouse.y;
        box.style.setProperty("--mouse-x", `${boxX}px`);
        box.style.setProperty("--mouse-y", `${boxY}px`);
      });
    }
  }, [isMobile, mouse, boxes, containerRef, containerSize]);

  // 1) Yüklenince child elementleri al
  useEffect(() => {
    if (!containerRef.current) return;
    const els = Array.from(containerRef.current.children) as HTMLElement[];
    setBoxes(els);
    
    // Mobil cihaz kontrolü
    setIsMobile('ontouchstart' in window || 
      navigator.maxTouchPoints > 0 || 
      window.innerWidth < 768);
  }, []);

  // 2) Container boyutunu ayarla, window resize
  useEffect(() => {
    if (typeof window === "undefined") return;

    function initContainer() {
      if (containerRef.current) {
        containerSize.current.w = containerRef.current.offsetWidth;
        containerSize.current.h = containerRef.current.offsetHeight;
      }
      
      // Ekran boyutu değiştiğinde mobil kontrolü güncelle
      setIsMobile('ontouchstart' in window || 
        navigator.maxTouchPoints > 0 || 
        window.innerWidth < 768);
    }

    initContainer();
    window.addEventListener("resize", initContainer);

    return () => {
      window.removeEventListener("resize", initContainer);
    };
  }, [boxes]);

  // 3) Mouse move -> fare konumunu kaydet (sadece masaüstünde)
  useEffect(() => {
    if (typeof window === "undefined" || isMobile) return;

    function handleMouseMove(e: MouseEvent) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        setMouse({ x, y });
      }
    }

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [isMobile]);

  // 4) Fare konumu değiştikçe style güncelle (sadece masaüstünde)
  useEffect(() => {
    if (isMobile) return; 
    onMouseMove();
  }, [mouse, isMobile, onMouseMove]);

  return (
    <div className={className} ref={containerRef}>
      {children}
    </div>
  );
}